/**
 * FilterPanel Component
 *
 * A right-side sliding panel for displaying filter controls.
 * Includes sections for organizing filters by category.
 */

import React from 'react';
import { Box, Typography, IconButton, Drawer, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii, shadows, spacing } from '../../tokens';
import { Button } from '../Button/Button';

export interface FilterSectionProps {
    /** Section title */
    title: string;
    /** Section content */
    children: React.ReactNode;
}

export const FilterSection: React.FC<FilterSectionProps> = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
        <Typography
            variant='overline'
            sx={{
                display: 'block',
                color: colors.neutral[500],
                fontSize: typographyTokens.fontSize.xs,
                fontWeight: typographyTokens.fontWeight.semibold,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                mb: 1.5,
            }}>
            {title}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</Box>
    </Box>
);

export interface FilterPanelProps {
    /** Whether the panel is open */
    open: boolean;
    /** Handler for closing the panel */
    onClose: () => void;
    /** Panel title */
    title?: string;
    /** Panel content - filter controls */
    children: React.ReactNode;
    /** Handler for applying filters */
    onApply?: () => void;
    /** Handler for resetting filters */
    onReset?: () => void;
    /** Apply button text */
    applyText?: string;
    /** Reset button text */
    resetText?: string;
    /** Panel width */
    width?: number;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
    open,
    onClose,
    title = 'Filters',
    children,
    onApply,
    onReset,
    applyText = 'Apply Filters',
    resetText = 'Reset',
    width = 360,
    sx,
}) => (
    <Drawer
        anchor='right'
        open={open}
        onClose={onClose}
        PaperProps={{
            sx: {
                width,
                boxShadow: shadows.xl,
                ...sx,
            },
        }}>
        {/* Header */}
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2.5,
                py: 2,
                borderBottom: `1px solid ${colors.neutral[200]}`,
            }}>
            <Typography
                variant='h6'
                sx={{
                    fontFamily: typographyTokens.fontFamily.display,
                    fontWeight: typographyTokens.fontWeight.semibold,
                    fontSize: typographyTokens.fontSize.lg,
                    color: colors.neutral[900],
                }}>
                {title}
            </Typography>
            <IconButton
                onClick={onClose}
                size='small'
                sx={{
                    color: colors.neutral[500],
                    '&:hover': {
                        backgroundColor: colors.neutral[100],
                    },
                }}>
                <CloseIcon />
            </IconButton>
        </Box>

        {/* Content */}
        <Box
            sx={{
                flex: 1,
                overflowY: 'auto',
                px: 2.5,
                py: 2.5,
            }}>
            {children}
        </Box>

        {/* Footer Actions */}
        {(onReset || onApply) && (
            <Box
                sx={{
                    display: 'flex',
                    gap: 1.5,
                    px: 2.5,
                    py: 2,
                    borderTop: `1px solid ${colors.neutral[200]}`,
                    backgroundColor: colors.neutral[0],
                }}>
                {onReset && (
                    <Button variant='secondary' onClick={onReset} fullWidth>
                        {resetText}
                    </Button>
                )}
                {onApply && (
                    <Button variant='primary' onClick={onApply} fullWidth>
                        {applyText}
                    </Button>
                )}
            </Box>
        )}
    </Drawer>
);

export default FilterPanel;
